import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { Loader2, Video, MapPin, ExternalLink, Building2, Info } from "lucide-react";

export const AerialViewDialog = ({ route, onClose, onOpenQuote }) => {
  const open = !!route;
  const [mode, setMode] = useState("idle"); // idle | loading | video | street | none
  const [videoUrl, setVideoUrl] = useState(null);
  const [streetUrl, setStreetUrl] = useState(null);
  const [note, setNote] = useState("");
  const streetRef = useRef(null);

  const revokeStreet = () => {
    if (streetRef.current) { window.URL.revokeObjectURL(streetRef.current); streetRef.current = null; }
  };

  useEffect(() => {
    if (!open) {
      setMode("idle"); setVideoUrl(null); setStreetUrl(null); setNote("");
      revokeStreet();
    }
  }, [open]);

  useEffect(() => () => revokeStreet(), []);

  const loadStreet = async (msg) => {
    try {
      const res = await api.get("/map/streetview", { params: { quote_id: route.id }, responseType: "blob" });
      revokeStreet();
      const u = window.URL.createObjectURL(res.data);
      streetRef.current = u;
      setStreetUrl(u);
      setNote(msg);
      setMode("street");
    } catch {
      setNote("No 3D aerial or street-level imagery is available for this destination.");
      setMode("none");
    }
  };

  const loadAerial = async () => {
    if (!route?.id) return;
    setMode("loading");
    setNote("");
    try {
      const { data } = await api.get("/map/aerial", { params: { quote_id: route.id } });
      if (data.state === "active" && data.video_url) {
        setVideoUrl(data.video_url);
        setMode("video");
        return;
      }
      const msg = data.state === "processing"
        ? "Google is still rendering the 3D aerial flyover for this address — showing street view for now. Try again in a minute."
        : "No 3D aerial coverage for this exact address — showing the street view instead.";
      await loadStreet(msg);
    } catch (e) {
      toast.error(apiError(e));
      setMode("idle");
    }
  };

  if (!route) return null;

  const fmtPlace = (p) => (p ? [p.city, p.state].filter(Boolean).join(", ") : "");
  const lane = [fmtPlace(route.origin), fmtPlace(route.destination)].filter(Boolean).join(" → ");
  const status = (route.status || "").toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0" data-testid="aerial-dialog">
        <DialogDescription className="sr-only">Immersive 3D aerial flyover and street view for {route.customer}.</DialogDescription>
        <DialogHeader className="space-y-1 border-b border-border px-5 py-3 pr-12 text-left">
          <DialogTitle className="flex items-center gap-2 font-heading text-base">
            <Building2 className="h-5 w-5 text-[hsl(var(--primary))]" />
            {route.dest_company || route.customer}
            {route.has_bol && (
              <span className="rounded bg-[hsl(var(--success))]/15 px-1.5 py-0.5 text-[10px] font-bold text-[hsl(var(--success))]">
                {route.bol_type === "GlobalTranz" ? "BOL" : "PRO"} {route.bol_number || route.bol_pro_number}{route.bol_type ? ` · ${route.bol_type}` : ""}
              </span>
            )}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {route.dest_address1 ? `${route.dest_address1} · ` : ""}{lane} · <span className="font-semibold text-foreground">{money(route.value || 0)}</span> · {status}
          </p>
        </DialogHeader>

        <div className="relative aspect-video w-full bg-black">
          {mode === "idle" && (
            <div className="flex h-full flex-col items-center justify-center gap-4 bg-gradient-to-br from-zinc-900 to-zinc-800 px-6 text-center text-white">
              <div className="rounded-full bg-white/10 p-4">
                <Video className="h-8 w-8 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <p className="font-heading text-lg font-bold">3D Aerial Flyover</p>
                <p className="mt-1 max-w-md text-xs text-zinc-400">
                  See a cinematic aerial view of this destination{route.has_street_address ? " (exact street address)" : " (approximate, ZIP-level)"}. Loads on demand.
                </p>
              </div>
              <Button onClick={loadAerial} data-testid="aerial-view-btn" className="rounded-full">
                <Video className="mr-2 h-4 w-4" /> View 3D Aerial
              </Button>
            </div>
          )}

          {mode === "loading" && (
            <div className="flex h-full items-center justify-center text-white" data-testid="aerial-loading">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading view…
            </div>
          )}

          {mode === "video" && videoUrl && (
            <video
              src={videoUrl}
              autoPlay
              loop
              muted
              controls
              playsInline
              className="h-full w-full object-cover"
              data-testid="aerial-video"
            />
          )}

          {mode === "street" && streetUrl && (
            <img src={streetUrl} alt="Street view of destination" className="h-full w-full object-cover" data-testid="aerial-streetview" />
          )}

          {mode === "none" && (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-zinc-400" data-testid="aerial-none">
              <MapPin className="h-7 w-7" />
              <p className="max-w-sm text-sm">{note}</p>
            </div>
          )}

          {(mode === "video") && (
            <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur">
              Google Aerial View
            </span>
          )}
          {(mode === "street") && (
            <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur">
              Street View
            </span>
          )}
        </div>

        {mode === "street" && note && (
          <div className="flex items-start gap-2 border-t border-border bg-secondary/40 px-5 py-2 text-xs text-muted-foreground" data-testid="aerial-note">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {note}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <div className="flex items-center gap-2">
            {(mode === "video" || mode === "street" || mode === "none") && (
              <Button variant="outline" size="sm" onClick={loadAerial} data-testid="aerial-retry-btn">
                <Video className="mr-1.5 h-4 w-4" /> {mode === "video" ? "Reload" : "Retry 3D Aerial"}
              </Button>
            )}
          </div>
          <Button size="sm" onClick={() => onOpenQuote(route.id)} data-testid="aerial-open-quote-btn">
            Open Quote <ExternalLink className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AerialViewDialog;
